import React, { useEffect, useRef, type RefObject } from 'react';
import { useRoboticEyeScene } from '../hooks/useRoboticEyeScene';
import type { EmotionMode } from '../types/emotion';
import type { FaceSide } from '../utils/droneFace';
import type { ArmsMode } from '../utils/droneArms';
import type { BubbleLink, ChatSignal, CinemaLink } from '../types/chat';
interface RoboticEyeSceneProps {
  modelUrl: string;
  emotion: EmotionMode;
  screenScale: number;
  faceSide: FaceSide;
  armsMode: ArmsMode;
  chat: ChatSignal;
  /** Section the drone floats in (hero) */
  anchorRef: RefObject<HTMLElement>;
  /** Called when the drone docks to / leaves the bottom-right corner */
  onDockChange: (docked: boolean) => void;
  /** Touched down in / about to leave the bottom-right corner */
  onDockLanded?: (landed: boolean) => void;
  /** Finished entering its current spot */
  onArrive: (arrived: boolean) => void;
  bubbleLink: BubbleLink;
  onBubblesVisible: (visible: boolean) => void;
  onBusyChange: (busy: boolean) => void;
  cinema: CinemaLink;
  /** Reports whether movable side plates were found on the model */
  onArmsFound?: (found: boolean) => void;
}

/** Transparent 3D layer that fills its parent; input is read from the window so it never blocks the page. */
export function RoboticEyeScene({
  modelUrl,
  emotion,
  screenScale,
  faceSide,
  armsMode,
  chat,
  anchorRef,
  onDockChange,
  onDockLanded,
  onArrive,
  bubbleLink,
  onBubblesVisible,
  onBusyChange,
  cinema,
  onArmsFound
}: RoboticEyeSceneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    status,
    armsFound
  } = useRoboticEyeScene(containerRef, {
    modelUrl,
    emotion,
    screenScale,
    faceSide,
    armsMode,
    chat,
    anchorRef,
    onDockChange,
    onDockLanded,
    onArrive,
    bubbleLink,
    onBubblesVisible,
    onBusyChange,
    cinema
  });
  useEffect(() => {
    if (armsFound !== null) onArmsFound?.(armsFound);
  }, [armsFound, onArmsFound]);
  return (
    // Page-wide fixed layer: the drone follows the hero, then docks bottom-right
    // Large-viewport height: stays the same size while a phone's address bar slides in and out, so
    // Pixel doesn't jump or rescale mid-scroll
    <div className="pointer-events-none fixed inset-x-0 top-0 z-40 h-[100lvh]" data-id="element-183" data-mp="3k2luywqopm3b">
      <div ref={containerRef} role="img" aria-label="Pixel, a floating drone with a pixelated red face showing his emotions." className="h-full w-full" data-id="element-184" data-mp="3p1q5bqpchpv8" />
      {status === 'error' && <p role="status" className="absolute inset-x-0 top-[33%] text-center text-xs font-semibold tracking-wide text-neutral-500" data-id="element-185" data-mp="2wecbw2kuvcfv">
          Model failed to load
        </p>}
    </div>
  );
}